import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

const BlogPost = ({ isPreview, slug: propSlug, onBack }) => {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const slug = isPreview ? propSlug : paramSlug;

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ['blogPost', slug],
    queryFn: async () => {
      const res = await axiosClient.get(`/public/blog/${slug}`);
      return res; 
    },
    retry: false
  });

  const normalizeHtml = (html = '') => {
    return html
        .replace(/text-align:[^;"]+;?/gi, '') // Remove alignments
        .replace(/margin-left:[^;"]+;?/gi, '') // Remove indentation
        .replace(/padding-left:[^;"]+;?/gi, '') // Remove padding

        // remove inline color styles (editor se aa rahe)
        .replace(/style="[^"]*color:[^";]*;?[^"]*"/gi, '')
        .replace(/color:\s*(black|#000|#000000)/gi, 'inherit')
        // fix empty paragraphs
        .replace(/<p>\s*<\/p>/gi, '')
        // ensure proper spacing
        .replace(/<\/p>\s*<p>/gi, '</p><p>')
        .trim();
    };

  if (isLoading) return <div className="min-h-screen pt-32 text-center text-slate-500 bg-[#020617] font-mono">Initializing Decryption...</div>;
  
  if (isError || !post) return (
    <div className="min-h-screen pt-32 text-center bg-[#020617] flex flex-col items-center">
      <h2 className="text-2xl font-bold text-white mb-2">Article Not Found</h2>
      <p className="text-slate-500 text-sm">The requested intelligence entry does not exist.</p>
      <button onClick={() => isPreview ? onBack() : navigate('/blog')} className="text-teal-500 mt-6 hover:text-teal-400 font-bold text-sm border border-blue-500/30 px-6 py-2 rounded-lg hover:bg-blue-500/10 transition-all">
        Return to Feed
      </button>
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-[#020617] pt-24 pb-20 overflow-x-hidden">
      
      {/* Container Start */}
      <div className="max-w-4xl mx-auto px-6">
        
        {/* 1. Navigation */}
        <button 
            onClick={() => isPreview ? onBack() : navigate('/blog')} 
            className="group flex items-center text-slate-400 hover:text-white mb-8 transition-colors text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Intelligence
        </button>

        {/* 2. Featured Image (Moved inside the 4xl constraint for perfect alignment) */}
        {post.featured_image_url && (
          <div className="mb-12 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl ring-1 ring-white/5">
              <img 
                src={post.featured_image_url} 
                alt={post.title} 
                className="w-full h-auto max-h-[500px] object-cover"
                onError={(e) => {
                  console.error("Image failed to load:", e.target.src);
                  e.target.parentElement.style.display = 'none';
                }}
              />
          </div>
        )}

        {/* 3. Hero Titles */}
        <h1 className="text-4xl md:text-5xl font-black text-white mb-6 leading-[1.2] tracking-tight">
          {post.title}
        </h1>
        
        {post.subtitle && (
          <p className="text-xl text-slate-400 mb-8 leading-relaxed font-light border-l-4 border-blue-600 pl-4">
            {post.subtitle}
          </p>
        )}

        {/* Meta Data Bar */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 border-b border-slate-800 pb-8 mt-6">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full flex items-center justify-center text-white font-bold mr-3 shadow-lg shadow-blue-900/20 text-xs">
              {post.author_name ? post.author_name[0] : 'A'}
            </div>
            <span className="text-slate-300 font-medium">{post.author_name || 'AIRS Command'}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
            {new Date(post.published_at || post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <div className="flex items-center">
            <span className="flex items-center px-3 py-1 bg-slate-800/50 rounded-full text-teal-400 font-bold text-[10px] border border-slate-700 uppercase tracking-wider">
              <Tag className="w-3 h-3 mr-1.5" />
              {post.category || 'Update'}
            </span>
          </div>
        </div>
      </div>


      {/* 3. Main Content (Typography Fixes) */}
     <article className="max-w-5xl mx-auto px-6 overflow-hidden">
        <div
          className="
            whitespace-normal
            hyphens-none
            break-normal

            /* 🔥 FINAL WORD FIX */
            [word-break:normal]
            [overflow-wrap:break-word]
            [hyphens:manual]

            /* FORCE ALIGNMENT */
            text-left !text-left
            [&_*]:!text-left

            /* FORCED TEXT COLOR OVERRIDES */
            text-slate-300 [&_*]:text-slate-300
            
            /* TYPOGRAPHY SETTINGS (Tailwind Prose) */
            prose prose-lg prose-invert max-w-none

            /* HEADINGS */
            prose-headings:text-white prose-headings:font-bold prose-headings:leading-tight
            prose-h1:text-4xl prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-6
            prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-4
            
            /* PARAGRAPHS - Improved Readability */
            prose-p:text-slate-300 prose-p:leading-8 prose-p:mb-6 prose-p:!indent-0
            
            /* LINKS */
            prose-a:text-teal-400 hover:prose-a:text-teal-300 prose-a:no-underline prose-a:font-medium
            
            /* LISTS */
            prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-8 prose-li:text-slate-300 prose-li:pl-2 prose-li:mb-2
            prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-8
            
            /* BLOCKQUOTES */
            prose-blockquote:border-l-4 prose-blockquote:border-blue-500 
            prose-blockquote:bg-slate-800/30 prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:rounded-r-lg
            prose-blockquote:not-italic prose-blockquote:text-slate-200 prose-blockquote:mb-10

            /* IMAGES */
            prose-img:rounded-xl prose-img:shadow-lg prose-img:border prose-img:border-slate-800
            
            /* SPACING FIXES */
            [&>p]:mb-6 [&>ul]:mb-6 [&>ol]:mb-6/* PRE/CODE (Scrollable if too wide) */
            prose-pre:w-full prose-pre:overflow-x-auto prose-pre:bg-slate-900 prose-pre:p-4 prose-pre:rounded-lg
          "
          dangerouslySetInnerHTML={{
            __html: normalizeHtml(post.content_html).replace(/color:\s*black/gi, 'color: inherit').replace(/color:\s*#000000/gi, 'color: inherit')}}
          
        />
      </article>

      {/* Footer / CTA Area */}
      <div className="max-w-5xl mx-auto px-6 mt-20 pt-10 border-t border-slate-800 text-center">
          <p className="text-slate-500 text-sm mb-4">Enjoyed this analysis?</p>
          <button onClick={() => navigate('/register')} className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-500/20">
             Join the AIRS Network
          </button>
      </div>

    </div>
  );
};

export default BlogPost;